import React, { useState, useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  TextInput,
  FlatList,
  Image,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import t from "@constants/localization";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import SearchField from "./components/SearchField";
import Profile from "@assets/svg/MyDeliveries/Profile";
import Filter from "@assets/svg/MyDeliveries/Filter";
import FilterModal from "@components/FilterModal";
import ButtonError from "@assets/svg/DbManagement/Error";
import { getOverviewItemDetails } from "@services/api";
interface BaseItem {
  title?: string;
  search_key?: string;
}

interface ProductItem extends BaseItem {
  product_name: string;
  return_reason: string;
  return_reason_txt: string;
  quantity: number;
  amount: number;
  amount_txt: string;
  product_image?: string;
  damaged_quantity: number;
  amount_tag: string;
  amount_tag_text: string;
  damage_quantity_strip: string;
}

interface CrateItem extends BaseItem {
  title: string;
  amount_tag_text: string;
  amount_tag: string;
}

interface PacketItem extends BaseItem {
  title: string;
  shipment_id: string;
  user_name: string;
  product_image?: string;
}
interface BagItem extends BaseItem {
  amount_tag: string;
  amount_tag_text: string;
}

type ItemType = ProductItem | CrateItem | PacketItem;

interface OverviewDetailProps {
  route: {
    params: {
      title: string;
      type: string;
      id: string;
    };
  };
  navigation: any;
}

const PATH = "screens/MyDeliveries/Overview/OverviewDetail";

const OverviewDetail: React.FC<OverviewDetailProps> = ({
  route,
  navigation,
}) => {
  const { title, type, id } = route.params;
  const [searchQuery, setSearchQuery] = useState("");
  const [cardType, setCardType] = useState("ITEM");
  const [items, setItems] = useState<ItemType[]>([]);
  const [filteredItems, setFilteredItems] = useState<ItemType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const textInputRef = useRef<TextInput>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<string>("return_reason");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any[]>>(
    {},
  );
  const [filterData, setFilterData] = useState(null);
  const [searchPlaceholder, setSearchPlaceholder] = useState("Search");

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await getOverviewItemDetails({ id, type });
        setItems(response.data.items);
        setFilteredItems(response.data.items);
        setFilterData(response.data.filters);
        setCardType(response.data.item_type);
        if (response.data.filters)
          setSelectedCategory(Object.keys(response.data.filters.options)[0]);
        setSearchPlaceholder(response.data.search_placeholder);
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [id, type]);

  useEffect(() => {
    const searchResults = items.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.search_key?.toLowerCase().includes(searchTerm);
    });
    const filteredResults = applyFilters(searchResults);
    setFilteredItems(filteredResults);
  }, [searchQuery, items]);

  const applyFilters = (itemsToFilter: ItemType[]) => {
    if (Object.keys(selectedFilters).length === 0) return itemsToFilter;

    return itemsToFilter.filter((item) => {
      const productItem = item as ProductItem;

      // Check each filter category
      for (const [category, selectedValues] of Object.entries(
        selectedFilters,
      )) {
        if (!selectedValues || selectedValues.length === 0) continue;

        let isDamageMarked;

        switch (category) {
          case "return_reason":
            if (
              !selectedValues.some(
                (filter) => filter === productItem.return_reason,
              )
            ) {
              return false;
            }
            break;

          case "damage": {
            isDamageMarked = productItem.damaged_quantity > 0;
            if (selectedValues.includes("DAMAGE_MARKED") && !isDamageMarked) {
              return false;
            }
            break;
          }

          case "amount": {
            if (
              (selectedValues.includes("AMOUNT_CREDIT") &&
                productItem?.amount_tag !== "AMOUNT_CREDIT") ||
              (selectedValues.includes("AMOUNT_DEBIT") &&
                productItem?.amount_tag !== "AMOUNT_DEBIT")
            ) {
              return false;
            }
            break;
          }
        }
      }
      return true;
    });
  };

  const handleFilterSave = () => {
    const searchResults = items.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.search_key?.toLowerCase().includes(searchTerm);
    });

    const filteredResults = applyFilters(searchResults);
    setFilteredItems(filteredResults);
    setShowFilterModal(false);
  };
  const numberOfFilters = () => {
    let count = 0;
    Object.values(selectedFilters).forEach((filters) => {
      count += filters.length;
    });
    return count;
  };

  const onPressCrossIcon = () => {
    setSearchQuery("");
    textInputRef.current?.clear();
  };
  const handleOptionPress = (item: any) => {
    setSelectedFilters((prev) => {
      const currentFilters = { ...prev };
      if (currentFilters[selectedCategory]?.includes(item)) {
        currentFilters[selectedCategory] = [];
      } else {
        currentFilters[selectedCategory] = [item];
      }

      return currentFilters;
    });
  };

  const getSelectedFilters = () => {
    return selectedFilters[selectedCategory] || [];
  };

  const clearFilters = () => {
    setSelectedFilters({});
    const searchResults = items.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.search_key?.toLowerCase().includes(searchTerm);
    });
    setFilteredItems(searchResults);
  };

  const renderProductItem = (item: ProductItem) => (
    <View>
      <View style={styles.itemContainer}>
        <View style={styles.imageContainer}>
          {item.product_image ? (
            <Image source={{ uri: item.product_image }} style={styles.image} />
          ) : (
            <View style={styles.placeholderImage}>
              <Text variant="caption_1">{item.product_name}</Text>
            </View>
          )}
          <View style={styles.quantityCirle}>
            <Text variant="caption_1" style={styles.quantityText}>
              {item.quantity}
            </Text>
          </View>
        </View>
        <View style={styles.itemDetails}>
          <Text variant="caption_2" style={styles.itemTitle} numberOfLines={2}>
            {item.product_name}
          </Text>
          <Text variant="caption_1" style={styles.price}>
            {item.amount_txt}
          </Text>
          <View style={styles.itemFooter}>
            {item.return_reason ? (
              <View style={styles.statusBadge}>
                <Text variant="caption_3" style={styles.statusText}>
                  {item.return_reason_txt}
                </Text>
              </View>
            ) : null}
            {item.amount_tag_text ? (
              <View style={[styles.statusBadge, styles.creditBadge]}>
                <Text
                  variant="caption_3"
                  style={[styles.statusText, styles.primaryText]}
                >
                  {item.amount_tag_text}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
      {item.damage_quantity_strip ? (
        <View style={styles.damageContainer}>
          <ButtonError />
          <Text variant="caption_2" style={styles.damageText}>
            {item.damage_quantity_strip}
          </Text>
        </View>
      ) : null}
    </View>
  );

  const renderCrateItem = (item: CrateItem) => (
    <View style={styles.itemContainer}>
      <View style={styles.crateDetails}>
        <Text variant="body_text_2" style={styles.itemTitle}>
          {item.title}
        </Text>
        {item.amount_tag_text ? (
          <View style={[styles.statusBadge2, styles.creditBadge]}>
            <Text
              variant="caption_3"
              style={[styles.statusText, styles.primaryText]}
            >
              {item.amount_tag_text}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );

  const renderPacketItem = (item: PacketItem) => (
    <View style={styles.packetItemContainer}>
      <View style={styles.packetImageContainer}>
        <Image source={{ uri: item.product_image }} style={styles.image} />
      </View>
      <View style={styles.itemDetails}>
        <Text variant="caption_1" style={styles.itemTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <View style={styles.itemFooter}>
          <Profile />
          <Text variant="caption_3" style={styles.userNameText}>
            {item.user_name}
          </Text>
        </View>
        <View style={styles.itemFooter}>
          <Text variant="caption_3" style={styles.shipmentId}>
            {`Shipment No : ${item.shipment_id}`}
          </Text>
        </View>
      </View>
    </View>
  );
  const renderBagItem = (item: BagItem) => (
    <View style={styles.itemContainer}>
      <View style={styles.crateDetails}>
        <Text variant="body_text_2" style={styles.itemTitle}>
          {item.title}
        </Text>
        {item.amount_tag_text ? (
          <View style={[styles.statusBadge2, styles.creditBadge]}>
            <Text
              variant="caption_3"
              style={[styles.statusText, styles.primaryText]}
            >
              {item.amount_tag_text}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );

  const renderItem = ({ item }: { item: ItemType }) => {
    switch (cardType) {
      case "ITEM":
        return renderProductItem(item as ProductItem);
      case "CRATE":
        return renderCrateItem(item as CrateItem);
      case "PACKET":
        return renderPacketItem(item as PacketItem);
      case "BAG":
        return renderBagItem(item as BagItem);
      default:
        return null;
    }
  };

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={title}
        headerColor={color.white}
        navigation={navigation}
        route={route}
        rightComponent={
          filterData ? (
            <TouchableOpacity
              activeOpacity={0.8}
              style={styles.rightComponent}
              onPress={() => setShowFilterModal(true)}
            >
              {numberOfFilters() > 0 && (
                <View style={styles.filterBadge}>
                  <Text variant="caption_1" style={styles.badgeText}>
                    {numberOfFilters()}
                  </Text>
                </View>
              )}
              <Filter />
              <Text variant="body_text_3" style={styles.filterText}>
                {t(`${PATH}/filter`)}
              </Text>
            </TouchableOpacity>
          ) : (
            <View />
          )
        }
      />
      <View style={styles.searchContainer}>
        <SearchField
          searchPlaceholder={searchPlaceholder}
          searchText={searchQuery}
          setSearchText={setSearchQuery}
          onPressCrossIcon={onPressCrossIcon}
          onSearchInputChange={(text) => setSearchQuery(text)}
          testID="mt-search-field"
          hideShadow={false}
          textInputRef={textInputRef}
        />
      </View>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      ) : (
        <FlatList
          data={filteredItems}
          renderItem={renderItem}
          keyExtractor={(item, index) =>
            "shipment_id" in item ? item.shipment_id : index.toString()
          }
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
        />
      )}
      <FilterModal
        visible={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onSave={handleFilterSave}
        filterData={filterData}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        handleOptionPress={handleOptionPress}
        getSelectedFilters={getSelectedFilters}
        clearFilters={clearFilters}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.bgcolor,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingTop: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  listContainer: {
    padding: 16,
  },
  itemContainer: {
    flexDirection: "row",
    backgroundColor: color.white,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: color.primaryBg,
    paddingVertical: 12,
  },
  imageContainer: {
    backgroundColor: color.white,
    borderRadius: 12,
    padding: 8,
    justifyContent: "center",
    marginRight: 12,
  },
  image: {
    width: 60,
    height: 60,
  },
  placeholderImage: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: color.primaryBg,
  },
  itemDetails: {
    flex: 1,
    marginLeft: 12,
    justifyContent: "space-between",
  },
  crateDetails: {
    flex: 1,
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
  },
  itemTitle: {
    color: color.black06,
  },
  itemFooter: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  price: {
    color: color.mediumBlacklightBlack,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: color.lighterBlack,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
    marginTop: 4,
  },
  statusBadge2: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: color.lighterBlack,
    justifyContent: "center",
    alignItems: "center",
  },
  statusText: {
    color: color.mediumBlack,
    fontWeight: "bold",
  },
  shipmentId: {
    color: color.black06,
  },
  quantityCirle: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    backgroundColor: color.mediumBlack,
    top: -8,
    right: -6,
    padding: 2,
    paddingHorizontal: 6,
  },
  quantityText: {
    color: color.white,
    lineHeight: 16,
    top: 2,
  },
  rightComponent: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: color.mediumBlacklightBlack,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginRight: 4,
  },
  filterText: {
    marginLeft: 4,
  },
  creditBadge: {
    backgroundColor: "rgba(228, 0, 70, 0.05)",
  },
  primaryText: {
    color: color.primary,
  },
  damageContainer: {
    backgroundColor: "rgba(255, 228, 228, 1)",
    flexDirection: "row",
    borderBottomEndRadius: 12,
    borderBottomStartRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
    paddingTop: 16,
    zIndex: -1,
    top: -16,
    alignContent: "flex-start",
  },
  damageText: {
    color: color.mediumBlack,
    marginLeft: 6,
  },
  packetItemContainer: {
    backgroundColor: color.white,
    borderRadius: 12,
    marginBottom: 12,
    elevation: 2,
    flexDirection: "row",
    padding: 6,
    paddingVertical: 10,
  },
  packetImageContainer: {
    borderRadius: 6,
    padding: 0,
    marginRight: 0,
    position: "relative",
  },
  userNameText: {
    marginLeft: 5,
  },
  filterBadge: {
    backgroundColor: "rgba(25, 129, 85, 1)",
    padding: 2,
    borderRadius: 20,
    position: "absolute",
    top: -10,
    right: -4,
    minWidth: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: color.white,
    fontSize: 13,
    lineHeight: 18,
  },
});

export default OverviewDetail;
